"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Introduction() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const projects = [{
    name: "Modern Workspace",
    location: "DOWNTOWN",
    year: "2024",
    materials: "Polished concrete, acoustic felt, modular steel",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/modern_workspace_interior.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/modern_workspace_interior.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/modern_workspace_interior.png-wvc-sizes",
    alt: "Modern workspace interior with polished concrete and acoustic panels"
  }, {
    name: "Urban Loft",
    location: "METRO",
    year: "2024",
    materials: "Exposed brick, matte black metal, walnut wood",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/urban_loft_design.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/urban_loft_design.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/urban_loft_design.webp-wvc-sizes",
    alt: "Urban loft design featuring exposed brick and walnut accents"
  }, {
    name: "Creative Studio",
    location: "DISTRICT",
    year: "2023",
    materials: "White oak, glass partitions, soft ambient lighting",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/creative_studio_space.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/creative_studio_space.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/creative_studio_space.webp-wvc-sizes",
    alt: "Creative studio space with white oak and glass partitions"
  }, {
    name: "Executive Suite",
    location: "PLAZA",
    year: "2023",
    materials: "Italian marble, brushed brass, velvet upholstery",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/executive_suite_luxury.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/executive_suite_luxury.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/executive_suite_luxury.png-wvc-sizes",
    alt: "Executive suite with Italian marble and brushed brass finishes"
  }];
  return <section data-section-id="3203" id="hero-introduction" className="bg-background text-foreground py-24 md:py-32">
      <div className="container mx-auto px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-10 md:mb-14 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <p className="text-xs md:text-sm font-mono uppercase mb-8" style={{
            color: "var(--secondary)",
            letterSpacing: "0.25em"
          }}>—Featured Projects</p>
            <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] text-foreground max-w-2xl tracking-tight">
              Design solutions,{" "}
              <em className="italic" style={{
              color: "var(--secondary)"
            }}>
                thoughtfully
              </em>{" "}
              executed.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/ServicesPage" className="group inline-flex items-center gap-2 text-sm md:text-base font-default text-foreground">
              
              
            </Link>
          </div>
        </div>

        {}
        

        {}
        <div className="lg:hidden flex flex-col gap-16 md:gap-20">
          {projects.map((project, i) => <ProjectCard data-index={i} key={i} project={project} index={i} className="" aspectClass={i === 0 ? "aspect-[16/11]" : i === 1 ? "aspect-[3/4]" : i === 2 ? "aspect-square" : "aspect-[4/3]"} />)}
        </div>
      </div>
    </section>;
}
type Project = {
  name: string;
  location: string;
  year: string;
  materials: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
function ProjectCard({
  project,
  index,
  className,
  aspectClass,
  ...rest
}: {
  project: Project;
  index: number;
  className?: string;
  aspectClass: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className={`group ${className} transition-all duration-[1100ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} {...rest}>
      <Link to="/ServicesPage" className="block">
        {}
        <div className={`relative overflow-hidden rounded-lg bg-muted ${aspectClass}`}>
          
          {}
          <div className="absolute inset-0 mix-blend-overlay opacity-30 pointer-events-none" style={{
          backgroundImage: "radial-gradient(hsla(210, 10%, 15%, 0.25) 1px, transparent 1px)",
          backgroundSize: "3px 3px"
        }} />
          {}
          <div className="absolute top-5 right-5 w-10 h-10 rounded-lg bg-background/80 backdrop-blur-sm flex items-center justify-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-out">
            <ArrowUpRight className="w-4 h-4 text-foreground" strokeWidth={1.5} />
          </div>
        </div>

        {}
        <div className="mt-6 h-px w-full bg-border" />

        {}
        <div className="mt-5 space-y-2">
          <p className="text-xs font-mono uppercase" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.2em"
        }}>
            {project.location} / {project.year}
          </p>
          <h3 className="font-default font-light text-2xl leading-tight" style={{
          color: "var(--foreground)"
        }}>
            {project.name}
          </h3>
          <p className="italic text-[0.9rem] font-default leading-relaxed" style={{
          color: "var(--secondary)"
        }}>
            {project.materials}
          </p>
        </div>
      </Link>
    </div>;
}